import { z } from "zod";
import { I18nDisplayName } from "@/00-shared/type";

enum AtomicFieldType {
  TEXT = 1,
  URL = 2,
  NUMBER = 3,
  TIMESTAMP = 4,
  DATETIME = 5,
  CHECKBOX = 6,
  OPTION = 7,
  CASCADER = 8,
  CURRENCY = 9,
  IMAGE = 10,
  NUMBER_SERIES = 11,
  CURRENCY_SERIES = 12,
  COUNTRY = 13,
  ENUMERATOR = 14,
  ENTITY_ID = 15,
  SKU = 16,
  PRICE = 17,
  SELLER_PROP = 18,
  JSON_ = 19,
  CONDITION_NUMBER = 20,
  Record_URL = 21,
  REVIEW_TAGS = 22,
  PARTNERED_BRAND = 24,
  REVIEWOPINION = 25,
}
enum SortOrder {
  ASC = 0,
  DESC = 1,
}
export enum Relation {
  AND = 1,
  OR = 2,
  NOT = 3,
}

export enum Operator {
  BETWEEN = 1,
  IN = 2,
  GREATER = 3,
  LESS = 4,
  EQUAL = 5,
  EXIST = 6,
  DOES_NOT_EXIST = 11,
  LIKE = 12,
  NOT_LIKE = 13,
  NOT_EQUAL = 14,
  NOT_IN = 15,
  NOT_LESS_THAN = 16,
  NOT_GREATER_THAN = 17,
  REGEXP = 18,
  CLOSED_OPEN_INTERVAL = 20,
}

export enum TimeGranularity {
  DAILY = 0,
  WEEKLY = 1,
  MONTHLY = 2,
  MINUTE = 3,
}

const stringNumberBoolean = z.union([z.string(), z.number(), z.boolean()]);

const selectedFieldWithParams = z.object({
  field_key: z.string(),
  filter_for_sub_field: z
    .object({
      field_key: z.string().optional(),
      operator: z.nativeEnum(Operator).optional(),
      value: z
        .union([stringNumberBoolean, stringNumberBoolean.array()])
        .optional(),
      filter_key: z.string().optional(),
      filter_option_name: z.string().optional(),
    })
    .array()
    .optional(),
  time_series_aggregation_params: z
    .object({
      aggregation_function: z.number().int().gte(1).lte(6).optional(),
    })
    .optional(),
  pop_growth_params: z
    .object({
      calculation_method: z.number().int().gte(1).lte(3),
      time_range: z
        .object({
          start_date: z.string(),
          end_date: z.string().optional(),
        })
        .optional(),
      dynamic_time_range: z.string().optional(),
      comparison_method: z.number().int().gte(1).lte(3).optional(),
    })
    .optional(),
  es_field_aggregation_params: z
    .object({
      time_range: z.object({
        start_date: z.string(),
        end_date: z.string().optional(),
      }),
      aggregation_function: z.number().int().gte(1).lte(6).optional(),
    })
    .optional(),
  es_multiple_field_aggregation_params: z
    .object({
      sub_field_key: z.string(),
      dimension: z.boolean().optional(),
      aggregation_function: z.number().int().gte(1).lte(6).optional(),
    })
    .array()
    .optional(),
  vm_range_query_params: z
    .object({
      start_date: z.string(),
      end_date: z.string().optional(),
      time_granularity: z.nativeEnum(TimeGranularity).optional(),
    })
    .optional(),
});

export const basicFilterType = z
  .object({
    field_key: z.string().optional(),
    operator: z.nativeEnum(Operator).optional(),
    value: z
      .union([stringNumberBoolean, stringNumberBoolean.array()])
      .optional(),
    filter_key: z.string().optional(),
    filter_option_name: z.string().optional(),
  })
  .array()
  .optional();
export type basicFilterType = z.infer<typeof basicFilterType>;

const customizedTimeFilter = z.array(
  z.object({
    field_key: z.string(),
    start_date_time: z.string().optional(),
    end_date_time: z.string().optional(),
    last_day: z.string().optional(),
  }),
);

export type CustomizedTimeFilter = z.infer<typeof customizedTimeFilter>;
export const SearchEntityParams = z.object({
  entity_type_name: z.string(),
  entity_filter: z
    .object({
      relation: z.nativeEnum(Relation),
      basic_filter: z
        .object({
          field_key: z.string().optional(),
          operator: z.nativeEnum(Operator).optional(),
          value: z
            .union([stringNumberBoolean, stringNumberBoolean.array()])
            .optional(),
          filter_key: z.string().optional(),
          filter_option_name: z.string().optional(),
        })
        .array()
        .optional(),
      filters: z
        .object({
          relation: z.nativeEnum(Relation),
          basic_filter: z
            .object({
              field_key: z.string().optional(),
              operator: z.nativeEnum(Operator).optional(),
              value: z
                .union([stringNumberBoolean, stringNumberBoolean.array()])
                .optional(),
              filter_key: z.string().optional(),
              filter_option_name: z.string().optional(),
            })
            .array()
            .optional(),
        })
        .array()
        .optional(),
    })
    .optional(),
  keyword_filter: z
    .object({
      keyword: z.string(),
      field_list: z.array(z.string()),
      case_insensitive: z.boolean().optional(),
    })
    .optional(),
  time_filter: z
    .object({
      granularity: z.nativeEnum(TimeGranularity),
      start_date_time: z.string(),
      end_date_time: z.string().optional(),
    })
    .optional(),
  sort_field_list: z
    .object({
      field: z.string(),
      sort_order: z.nativeEnum(SortOrder),
    })
    .array()
    .optional(),
  selected_field_list: z
    .array(z.union([z.string(), selectedFieldWithParams]))
    .optional(),
  entity_view_name: z.string().optional(),
  offset: z.number().optional(),
  limit: z.number().gte(0).lte(100).optional(),
  customized_time_filter: customizedTimeFilter.nullable().optional(),
});

export type SearchEntityParams = z.infer<typeof SearchEntityParams>;

const text = z.object({
  text: z
    .object({
      value: z.string(),
    })
    .optional(),
});

const currency = z.object({
  currency: z
    .object({
      value: z.string(),
      rendered_value_str: I18nDisplayName,
      currency_unit: z.string().optional(),
    })
    .optional(),
});
export const cascader = z.object({
  cascader: z
    .object({
      value: z
        .array(
          z.object({
            id: z.number().optional(),
            label: z.string(),
            value: I18nDisplayName,
            parent_id: z.number().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
});

export const country = z.object({
  country: z
    .object({
      country_code: z.string(),
      country_full_name: I18nDisplayName,
    })
    .optional(),
});

const customizedFieldEntityID = z.object({
  entity_id: z
    .object({
      value: z.string(),
    })
    .optional(),
});

const image = z.object({
  image: z
    .object({
      height: z.number().optional(),
      width: z.number().optional(),
      thumb_uri: z.string().optional(),
      thumb_url_list: z.array(z.string()).optional(),
      uri: z.string().optional(),
      url_list: z.array(z.string()).optional(),
    })
    .optional(),
});

const number = z.object({
  number: z
    .object({
      value: z.string(),
      value_str: I18nDisplayName,
    })
    .optional(),
});

const url = z.object({
  url: z
    .object({
      url: z.string(),
    })
    .optional(),
});
const live_recording = z.object({
  live_recording: z
    .object({
      url: z.string(),
    })
    .optional(),
});

const review_tags = z.object({
  review_tags: z
    .object({
      value: z.object({}).optional(),
    })
    .optional(),
});
const entityId = z.object({
  entity_id: z
    .object({
      value: z.string(),
    })
    .optional(),
});
const timestamp = z.object({
  timestamp: z
    .object({
      value: z.number(),
      value_str: z.string(),
    })
    .optional(),
});

const enumerator = z.object({
  enumerator: z
    .object({
      value: z.string(),
      display_name: I18nDisplayName.optional(),
    })
    .optional(),
});

const optionType = z.object({
  option: z.object({
    id: z.number(),
    label: z.string(),
    value: I18nDisplayName,
  }),
});

const conditionNumber = z.object({
  condition_number: z.object({
    value: z.string(),
    value_str: I18nDisplayName,
  }),
});
const partnered_brand = z.object({
  partner_brand: z
    .object({
      value: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
});

const review_opinion = z.object({
  review_opinions: z.object({
    value: z.array(
      z.object({
        opinion_type: z.string(),
        emotion_type: z.number(),
        highlight: z.array(z.string()),
        opinion_en: z.string(),
        opinion_zh: z.string(),
      }),
    ),
  }),
});

const atomicField = z.union([
  timestamp,
  number,
  conditionNumber,
  enumerator,
  text,
  currency,
  cascader,
  image,
  country,
  optionType,
  url,
  live_recording,
  review_tags,
  entityId,
  partnered_brand,
  review_opinion,
]);

export const EntityType1 = z.object({
  atomic_field_type: z.nativeEnum(AtomicFieldType),
});
export type ReviewOpinions = z.infer<
  typeof review_opinion
>["review_opinions"]["value"];

export type EntityType1 = z.infer<typeof EntityType1>;
// 将几个并行的字段改为 union
export const CustomizedField = z.object({
  atomic_field: atomicField.optional(),
  array: z.array(atomicField).optional(),
  analytics: z
    .record(z.array(z.union([timestamp.shape.timestamp, number.shape.number])))
    .optional(),
  field_key: z.string().optional(),
  entity_field_type: z.union([
    EntityType1,
    z.object({
      array: z.object({ atomic_field: z.nativeEnum(AtomicFieldType) }),
    }),
    z.object({
      analytics: z.object({
        sub_field: z.record(z.nativeEnum(AtomicFieldType)),
      }),
    }),
    z.object({
      time_series: z.object({
        value_type: z.nativeEnum(AtomicFieldType),
      }),
    }),
  ]),
});

export type CustomizedField = z.infer<typeof CustomizedField>;

export const Entity = z.object({
  entity_id: z.string(),
  default_field: z
    .object({
      product: z
        .object({
          product_id: z.object({
            value: z.string(),
          }),
        })
        .optional(),
      seller: z
        .object({
          seller_id: z.object({
            value: z.string(),
          }),
        })
        .optional(),
      creator: z
        .object({
          creator_id: z.object({
            value: z.string(),
          }),
        })
        .optional(),
      video: z
        .object({
          video_id: z.object({
            value: z.string(),
          }),
        })
        .optional(),
      ads: z
        .object({
          ad_id: z.object({
            value: z.string(),
          }),
        })
        .optional(),
    })
    .optional(),
  customized_field_list: z.array(CustomizedField).optional(),
});

export type Entity = z.infer<typeof Entity>;
export const SearchEntityResult = z.object({
  total_count: z.number(),
  entity_list: z.array(Entity),
  has_more: z.boolean(),
  highlight: z.array(z.string()).optional(),
});
export type SearchEntityResult = z.infer<typeof SearchEntityResult>;

export type CascaderOption = {
  id: number;
  label: string;
  value: I18nDisplayName;
  children?: CascaderOption[];
};

export const CascaderOption: z.ZodSchema<CascaderOption> = z.lazy(() =>
  z.object({
    id: z.number(),
    label: z.string(),
    value: I18nDisplayName,
    children: z.array(CascaderOption).optional(),
  }),
);

export const cascaderOption = z.object({
  value: z.array(
    z
      .object({
        id: z.number(),
        label: z.string(),
        value: I18nDisplayName,
        parent_id: z.number().optional(),
      })
      .optional(),
  ),
});
export const countryOption = z.object({
  country_code: z.string(),
  country_full_name: I18nDisplayName.optional(),
});

export const option = z.object({
  id: z.number(),
  label: z.string(),
  value: I18nDisplayName,
});

export const AvailableOptions = z
  .object({
    field_key: z.string().optional(),
    filter_key: z.string(),
    field_quick_option_name_list: z
      .array(
        z
          .object({
            zh: z.string().optional(),
            en: z.string().optional(),
            label: z.string(),
          })
          .passthrough(),
      )
      .optional(),
    field_rendered_value_list: z
      .array(z.union([cascaderOption, countryOption, option]))
      .optional(),
  })
  .optional();
export type AvailableOptions = z.infer<typeof AvailableOptions>;
export const AvailableOptionsList = z.array(AvailableOptions);
export type AvailableOptionsList = z.infer<typeof AvailableOptionsList>;
